import {createSlice, PayloadAction} from '@reduxjs/toolkit'

import type { RootState } from '../store'
import { THEMES } from '../../constants'
import { getService } from '../../helpers/ReduxHelpers'
import { ApiResponse } from '../api/types/state'
import { services } from '../../graphql'
import {
  AppAcceptDisclaimerMutationVariables,
  AppCancelAppointementMutationVariables,
  AppConfirmAppointementMutationVariables,
  AppGetStoresQuery,
  AppRdvUpdateMessageAppointmentMutationVariables,
} from '../../graphql/types/api'
import { Screen } from '../../types/global'

import { actionTypes } from './types'

//
// Services
//

const getStoresService = getService<
  'getStores',
  ApiResponse<typeof services.app.queries.getStores>['data'],
  AppGetStoresQuery
>('getStores')

const getDisclaimersService = getService<
  'getDisclaimers',
  ApiResponse<typeof services.app.queries.getDisclaimers>['data']
>('getDisclaimers')

const acceptDisclaimerService = getService<
  'acceptDisclaimer',
  ApiResponse<typeof services.app.mutations.acceptDisclaimer>['data'],
  AppAcceptDisclaimerMutationVariables
>('acceptDisclaimer')

const confirmAppointementService = getService<
  'confirmAppointement',
  ApiResponse<typeof services.app.mutations.comfirnAppointement>['data'],
  AppConfirmAppointementMutationVariables
>('confirmAppointement')

const cancelAppointementService = getService<
  'cancelAppointement',
  ApiResponse<typeof services.app.mutations.cancelAppointement>['data'],
  AppCancelAppointementMutationVariables
>('cancelAppointement')

const rdvUpdateMessageAppointmentService = getService<
  'rdvUpdateMessageAppointment',
  ApiResponse<typeof services.app.mutations.rdvUpdateMessageAppointment>['data'],
  AppRdvUpdateMessageAppointmentMutationVariables
>('rdvUpdateMessageAppointment')

//
// Initial state
//

export type AppState = {
  isInit: boolean
  screen: Screen
  theme: string
  getStores: typeof getStoresService.values
  getDisclaimers: typeof getDisclaimersService.values
  selectedAppointementId?: string
  appointementStep?: {
    firstName: string
    lastName: string
    email: string
    store: string
    date: string
    slot: string
  }
  acceptDisclaimer: typeof acceptDisclaimerService.values
  confirmAppointement: typeof confirmAppointementService.values
  cancelAppointement: typeof cancelAppointementService.values
  rdvUpdateMessageAppointment: typeof rdvUpdateMessageAppointmentService.values
}

const initialState: AppState = {
  isInit: false,
  theme: THEMES.DEFAULT,
  screen: Screen.appointement,
  getStores: getStoresService.values,
  getDisclaimers: getDisclaimersService.values,
  appointementStep: undefined,
  acceptDisclaimer: acceptDisclaimerService.values,
  confirmAppointement: confirmAppointementService.values,
  cancelAppointement: cancelAppointementService.values,
  rdvUpdateMessageAppointment: rdvUpdateMessageAppointmentService.values,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    init: () => undefined,
    resetSteps: () => undefined,
    setIsInit: (state, action: actionTypes.setIsInit) => {
      const { isInit } = action.payload
      state.isInit = isInit
    },
    setScreen: (state, { payload }: actionTypes.setScreen) => {
      state.screen = payload.screen
    },
    ...getStoresService.reducers,
    ...getDisclaimersService.reducers,
    setAppointementStep: (state, { payload }: actionTypes.setAppointementStep) => {
      state.appointementStep = payload
    },
    setSelectedAppointementId: (state, { payload }: PayloadAction<string>) => {
      state.selectedAppointementId = payload
    },
    ...acceptDisclaimerService.reducers,
    ...confirmAppointementService.reducers,
    ...cancelAppointementService.reducers,
    ...rdvUpdateMessageAppointmentService.reducers,
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const isInit = (state: RootState) => root(state).isInit
const screen = (state: RootState) => root(state).screen
const theme = (state: RootState) => root(state).theme
const getStores = (state: RootState) => root(state).getStores
const getDisclaimers = (state: RootState) => root(state).getDisclaimers
const appointementStep = (state: RootState) => root(state).appointementStep
const selectedAppointementId = (state: RootState) => root(state).selectedAppointementId
const acceptDisclaimer = (state: RootState) => root(state).acceptDisclaimer
const confirmAppointement = (state: RootState) => root(state).confirmAppointement
const cancelAppointement = (state: RootState) => root(state).cancelAppointement
const rdvUpdateMessageAppointment = (state: RootState) => root(state).rdvUpdateMessageAppointment

export const selectors = {
  isInit,
  theme,
  screen,
  getStores,
  getDisclaimers,
  appointementStep,
  selectedAppointementId,
  acceptDisclaimer,
  confirmAppointement,
  cancelAppointement,
  rdvUpdateMessageAppointment,
}
